/**
 * DegreeInterlinks — the four cross-page navigation chips that stop
 * the Feb 2026 course page being a dead end. Introduced in Phase 2 of
 * the 5-cluster restructure. All chips share the same visual
 * language: pill shape, tiny lucide icon, underline-on-hover, muted
 * stone-700 base. Never render without a valid target — if the data
 * we need is missing, the chip just doesn't paint.
 *
 * Chips:
 *   • `DegreeBreadcrumb`    — top of page. Home › Explore / Shortlist › {Uni} › {Course}. Back-link chooses the source from React Router `location.state.from`, else `document.referrer`, else falls back to Home.
 *   • `CityGuideLink`       — Cluster 3 (Costs & affordability). "See the full {city} student guide →" → `/cities/{slug}`.
 *   • `UniLifeLink`         — Cluster 5 (Will you enjoy it). "See what {uni} is like →" → `/uni/{id}?tab=life`.
 *   • `SimilarCoursesLink`  — Cluster 4 (Return on investment). "Compare this course across other unis →" → `/explore/cross-country?subject={subject}`.
 */
import { Link, useLocation } from "react-router-dom";
import { ChevronRight, MapPin, Sparkles, ArrowLeftRight, Home } from "lucide-react";

const linkClasses =
  "inline-flex items-center gap-2 text-sm font-medium text-stone-700 hover:text-stone-900 underline underline-offset-4 decoration-stone-400 hover:decoration-stone-900 transition-colors";

function citySlug(city) {
  if (!city) return "";
  return String(city)
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * Breadcrumb strip that hangs above the sticky course header. Reads
 * `location.state.from` first (set by <Link state={{ from: "/explore" }}> callers), then `document.referrer` from the same origin, else defaults to the Explore page — never falls back to `#` so the crumb is always live.
 */
export function DegreeBreadcrumb({ uni, degree }) {
  const location = useLocation();
  const stateFrom = location.state?.from;

  const referrer = typeof document !== "undefined" ? document.referrer : "";
  let refPath = "";
  try {
    if (referrer) {
      const u = new URL(referrer);
      if (u.origin === window.location.origin) refPath = u.pathname + u.search;
    }
  } catch (_) {
    refPath = "";
  }

  const originPath = stateFrom || refPath;
  let originLabel = "Explore";
  let originHref = "/explore";
  if (originPath.startsWith("/shortlist")) {
    originLabel = "Shortlist";
    originHref = "/shortlist";
  } else if (originPath.startsWith("/advanced")) {
    originLabel = "Advanced search";
    originHref = "/advanced";
  } else if (originPath.startsWith("/uni/")) {
    originLabel = uni?.name ? `${uni.name} · courses` : "University";
    originHref = originPath;
  } else if (originPath.startsWith("/cities/")) {
    originLabel = "City guide";
    originHref = originPath;
  }

  const uniHref = uni?.id ? `/uni/${uni.id}` : null;
  const uniName = uni?.name;
  const degreeTitle = degree?.official_title || degree?.title || degree?.name;

  return (
    <nav
      data-testid="degree-breadcrumb"
      aria-label="Breadcrumb"
      className="max-w-6xl mx-auto px-4 pt-6 pb-2 text-xs text-stone-500 flex items-center flex-wrap gap-1.5"
    >
      <Link
        to="/"
        data-testid="breadcrumb-home"
        className="inline-flex items-center gap-1 hover:text-stone-800 hover:underline underline-offset-4"
      >
        <Home className="w-3 h-3" />
        Home
      </Link>
      <ChevronRight className="w-3 h-3 text-stone-300" aria-hidden />
      <Link
        to={originHref}
        data-testid="breadcrumb-origin"
        className="hover:text-stone-800 hover:underline underline-offset-4"
      >
        {originLabel}
      </Link>
      {uniHref && uniName && (
        <>
          <ChevronRight className="w-3 h-3 text-stone-300" aria-hidden />
          <Link
            to={uniHref}
            data-testid="breadcrumb-uni"
            className="hover:text-stone-800 hover:underline underline-offset-4 max-w-[24ch] truncate"
          >
            {uniName}
          </Link>
        </>
      )}
      {degreeTitle && (
        <>
          <ChevronRight className="w-3 h-3 text-stone-300" aria-hidden />
          <span
            data-testid="breadcrumb-current"
            className="text-stone-800 font-medium max-w-[36ch] truncate"
            aria-current="page"
          >
            {degreeTitle}
          </span>
        </>
      )}
    </nav>
  );
}

export function CityGuideLink({ city }) {
  if (!city) return null;
  const slug = citySlug(city);
  if (!slug) return null;
  return (
    <div className="mt-4">
      <Link
        to={`/cities/${slug}`}
        data-testid="city-guide-link"
        className={linkClasses}
      >
        <MapPin className="w-4 h-4" />
        See the full {city} student guide — cost of living, transport, safety, culture →
      </Link>
    </div>
  );
}

export function UniLifeLink({ uni }) {
  if (!uni?.id || !uni?.name) return null;
  return (
    <div className="mt-6">
      <Link
        to={`/uni/${uni.id}?tab=life`}
        data-testid="uni-life-link"
        className={linkClasses}
      >
        <Sparkles className="w-4 h-4" />
        See the {uni.name} Life tab — halls, clubs, food, city character →
      </Link>
    </div>
  );
}

export function SimilarCoursesLink({ subject }) {
  if (!subject) return null;
  return (
    <div className="mt-6">
      <Link
        to={`/explore/cross-country?subject=${encodeURIComponent(subject)}`}
        data-testid="similar-courses-link"
        className={linkClasses}
      >
        <ArrowLeftRight className="w-4 h-4" />
        Compare {subject} across UK · US · DE · FR · AU →
      </Link>
    </div>
  );
}
